import * as PouchDB from 'pouchdb/dist/pouchdb';
import upsert from 'pouchdb-upsert';
import { DbEntry } from '../../model/db-entry.model';
import { Injectable } from '@angular/core';
import { TimeUtils } from '../../../../utils/time.utils';
import { DatabaseDocument, DatabaseDocumentAttachments, DatabaseResultObjectWithAttachments } from './database-document.interface';

@Injectable()
export class StorageServiceProvider {
  db: any = null;

  constructor() {
    PouchDB.plugin(upsert);
    this.init();
  }

  private init() {
    this.db = new PouchDB('sellions_database', {
      adapter: 'idb',
      size: 20,
      androidDatabaseImplementation: 2,
      revs_limit: 1,
      auto_compaction: true,
    });
  }

  public get(key: string): Promise<any> {
    return this.db.get(key).then((document: DatabaseDocument) => {
      return document.value;
    });
  }

  public set(key: string, value: string): Promise<void> {
    return this.db.upsert(key, (document: DatabaseDocument) => {
      document.value = value;
      return document;
    });
  }

  public setObject(key: string, value: any): Promise<any> {
    return this.db.upsert(key, (document: DatabaseDocument) => {
      document.value = JSON.stringify(value);
      return document;
    });
  }

  public setObjectWithAttachments(key: string, value: any, files: File[]): Promise<DatabaseDocument> {
    return this.db.upsert(key, (document: DatabaseDocument) => {
      document.value = JSON.stringify(value);

      if (!document._attachments) {
        document._attachments = {};
      }

      for (let file of files) {
        document._attachments[file.name] = {
          content_type: file.type,
          data: file,
        };
      }

      return document;
    });
  }

  public setBulkObject(values: any[], keyFormulationFunction?: (value: any) => string): Promise<void> {
    return this.db
      .bulkDocs(
        values.map((value) => {
          return { value, _id: keyFormulationFunction(value) };
        }),
      )
      .catch((err: any) => console.error(err));
  }

  public getObject(key: string): Promise<any> {
    return this.db.get(key).then((document: DatabaseDocument) => {
      return document.value ? JSON.parse(document.value) : null;
    });
  }

  public getObjectWithAttachments<T>(key: string): Promise<DatabaseResultObjectWithAttachments<T>> {
    return this.db.get(key, { attachments: true }).then((document: DatabaseDocument) => {
      const object = document.value ? JSON.parse(document.value) : null;
      return { object, attachments: document._attachments };
    });
  }

  public remove(key: string): Promise<void> {
    return this.db.get(key).then((document) => this.db.remove(document));
  }

  // fetchFunc has to return an Observable
  public getOrFetch(key: string, fetchFunc: Function, timeout: number): Promise<string> {
    return this.get(key)
      .then((dbEntryJson: string) => {
        const dbEntry: DbEntry = JSON.parse(dbEntryJson);

        if (TimeUtils.getUnixTimestamp() - dbEntry.lastModTime >= timeout) {
          return fetchFunc().toPromise();
        } else {
          return dbEntry.value;
        }
      })
      .catch(() => fetchFunc().toPromise());
  }

  public flushCache() {
    this.db
      .query((doc: { value: { lastModTime: any } }, emit: Function) => doc.value && doc.value.lastModTime != null && emit(doc))
      .then((val: { rows: any[] }) => val.rows.forEach((cell) => this.db.remove(cell).catch(() => {})));
  }

  public clearStorage(): Promise<any> {
    return this.db.destroy().then(() => this.init());
  }
}



// WEBPACK FOOTER //
// ./src/modules/core/services/storage-service/storage-service.ts